'use client'

import { cn } from '../../utils/cn'
import { Check } from 'lucide-react'
import { Section } from '../layout/Section'
import { Container } from '../layout/Container'
import { Screenshot } from '../common/Screenshot'
import { FadeIn } from '../motion/FadeIn'
import { SlideIn } from '../motion/SlideIn'

interface ShowcaseFeature {
  title: string
  description: string
  screenshot: string
  reverse?: boolean
  bullets?: string[]
}

interface FeatureShowcaseProps {
  sectionNumber?: string
  heading: string
  subheading?: string
  features: ShowcaseFeature[]
}

export function FeatureShowcase({
  sectionNumber,
  heading,
  subheading,
  features
}: FeatureShowcaseProps) {
  return (
    <Section number={sectionNumber}>
      <Container>
        {/* Section Header */}
        <div className="text-center mb-20 max-w-3xl mx-auto">
          <FadeIn>
            <h2 className="headline-lg mb-4">{heading}</h2>
          </FadeIn>
          {subheading && (
            <FadeIn delay={0.1}>
              <p className="body-lg">{subheading}</p>
            </FadeIn>
          )}
        </div>

        {/* Features */}
        <div className="space-y-24">
          {features.map((feature, index) => (
            <div
              key={index}
              className={cn(
                'grid lg:grid-cols-2 gap-12 lg:gap-16 items-center',
                feature.reverse && 'lg:[&>*:first-child]:order-2'
              )}
            >
              {/* Content */}
              <FadeIn direction={feature.reverse ? 'right' : 'left'}>
                <div>
                  <h3 className="headline-md mb-4">
                    {feature.title}
                  </h3>

                  <p className="body-md mb-6">
                    {feature.description}
                  </p>

                  {feature.bullets && feature.bullets.length > 0 && (
                    <ul className="space-y-3">
                      {feature.bullets.map((bullet, bulletIndex) => (
                        <li
                          key={bulletIndex}
                          className="flex items-start gap-3 text-sm text-[var(--landing-text-secondary)]"
                        >
                          <Check className="h-5 w-5 text-[var(--accent)] flex-shrink-0 mt-0.5" />
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </FadeIn>

              {/* Screenshot */}
              <SlideIn direction={feature.reverse ? 'left' : 'right'}>
                <Screenshot
                  src={feature.screenshot}
                  alt={feature.title}
                  browserChrome
                  shadow
                />
              </SlideIn>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  )
}
